import { useState, useRef, useCallback, useEffect } from 'react';

function formatDisplay(totalSeconds) {
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = Math.floor(totalSeconds % 60);
  const tenths  = Math.floor((totalSeconds * 10) % 10);
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}.${tenths}`;
}

export function useTimer(onTick) {
  const [seconds, setSeconds]     = useState(0);
  const [isRunning, setIsRunning] = useState(false);
  const [display, setDisplay]     = useState('00:00.0');
  const intervalRef = useRef(null);
  const secondsRef  = useRef(0);

  const tick = useCallback(() => {
    secondsRef.current += 0.1;
    const s = parseFloat(secondsRef.current.toFixed(1));
    setSeconds(s);
    setDisplay(formatDisplay(s));
    if (onTick) onTick(s);
  }, [onTick]);

  const start = useCallback(() => {
    if (intervalRef.current) return;
    setIsRunning(true);
    intervalRef.current = setInterval(tick, 100);
  }, [tick]);

  const pause = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    setIsRunning(false);
  }, []);

  const reset = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
    secondsRef.current = 0;
    setSeconds(0);
    setDisplay('00:00.0');
    setIsRunning(false);
  }, []);

  const getSeconds = useCallback(() => secondsRef.current, []);

  const setTime = useCallback((s) => {
    secondsRef.current = s;
    setSeconds(s);
    setDisplay(formatDisplay(s));
  }, []);

  useEffect(() => {
    return () => {
      if (intervalRef.current) clearInterval(intervalRef.current);
    };
  }, []);

  return { seconds, isRunning, display, start, pause, reset, getSeconds, setTime };
}

export { formatDisplay };
export default useTimer;
